import { MapFacadeMaplibre } from "src/managers/MapFacadeMaplibre";
import { feature, featureCollection } from "@turf/helpers";
import { bbox, bboxPolygon, difference } from "@turf/turf";
import { setupJPBMapLayers } from "./JPBMapLayers";
import type { JPBReferenceProperty } from "./JPBState";
import { PriceMode, AreaUnit } from "./JPBState";
import { JPBManager } from "./JPBManager";
import type { GeoJSONSource, Map as MaplibreMap } from "maplibre-gl";
import { FeatureLayer } from "src/managers/WmsLayerFunc";

const MASK_SOURCE_ID = 'jpb-export-search-area-mask-source';
const MASK_LAYER_ID = 'jpb-export-search-area-mask';

type RenderJPBOverviewImageOptions = {
   mapStyle: any;
   referenceProperties: JPBReferenceProperty[];
   checkedDocumentIds: string[];
   bufferGeometry?: any | null;
};

type RenderJPBHeatmapImageOptions = {
   mapStyle: any;
   referenceProperties: JPBReferenceProperty[];
   checkedDocumentIds: string[];
   bufferGeometry?: any | null;
   priceRange?: { min: number; max: number };
};

// ============================================================================
// MARK: OVERVIEW IMAGE
// ============================================================================

export async function renderJPBOverviewImage(
   options: RenderJPBOverviewImageOptions
): Promise<Uint8Array | undefined> {
   const { mapStyle, referenceProperties, checkedDocumentIds, bufferGeometry } = options;

   const checkedProperties = referenceProperties.filter(prop =>
      checkedDocumentIds.includes(prop.documentID)
   );

   if (!checkedProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } = await createExportMap(mapStyle);

   try {
      const checked = new Set<string>(checkedDocumentIds);

      setupJPBMapLayers(exportMap, checkedProperties, checked, PriceMode.Standard, AreaUnit.SquareMeter, {
         onFeatureClick: () => { /* no-op in export map */ }
      });

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      await fitMapToFeatures(exportMap, checkedProperties);
      return await captureMapImage(exportMap);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

// ============================================================================
// MARK: OVERVIEW WITH JORDBUND IMAGE
// ============================================================================

export async function renderJPBOverviewWithJordbundImage(
   options: RenderJPBOverviewImageOptions
): Promise<Uint8Array | undefined> {
   const { mapStyle, referenceProperties, checkedDocumentIds, bufferGeometry } = options;

   const checkedProperties = referenceProperties.filter(prop =>
      checkedDocumentIds.includes(prop.documentID)
   );

   if (!checkedProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } = await createExportMap(mapStyle);

   try {
      await fitMapToFeatures(exportMap, checkedProperties);
      const currentZoom = exportMap.getZoom();
      const dynamicMinZoom = Math.min(currentZoom, 11);

      const jordbundLayer = new FeatureLayer({
         version: "1.3.0",
         label: "Jordbundskort_2024",
         host: "geodata.fvm.dk",
         path: "/geoserver/ows",
         layers: "Jordbunds_og_terraenforhold:Jordbundskort_2024",
         minZoom: dynamicMinZoom,
         maxZoom: 24
      });

      exportMapFacade.addFeatureLayer("Jordbundskort_2024", jordbundLayer);
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 200));

      const checked = new Set<string>(checkedDocumentIds);
      setupJPBMapLayers(exportMap, checkedProperties, checked, PriceMode.Standard, AreaUnit.SquareMeter, {
         onFeatureClick: () => { /* no-op in export map */ }
      });

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      await Promise.race([
         new Promise<void>((resolve) => {
            let idleCount = 0;
            const onIdle = () => {
               idleCount++;
               if (idleCount >= 2) {
                  exportMap.off("idle", onIdle);
                  setTimeout(() => resolve(), 1000);
               }
            };
            exportMap.on("idle", onIdle);
            exportMap.triggerRepaint();
         }),
         new Promise<void>((resolve) => {
            setTimeout(() => {
               console.warn("Jordbund layer loading timeout - proceeding anyway");
               resolve();
            }, 10000);
         })
      ]);

      const mapCanvas = exportMap.getCanvas();
      const compositeCanvas = document.createElement('canvas');
      compositeCanvas.width = mapCanvas.width;
      compositeCanvas.height = mapCanvas.height;
      const compositeCtx = compositeCanvas.getContext('2d');

      if (!compositeCtx) {
         return await captureMapImage(exportMap);
      }

      compositeCtx.drawImage(mapCanvas, 0, 0);
      await drawJordbundLegend(compositeCanvas);
      return await captureCanvasImage(compositeCanvas);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

// ============================================================================
// MARK: HEATMAP IMAGE
// ============================================================================

export async function renderJPBHeatmapImage(
   options: RenderJPBHeatmapImageOptions
): Promise<Uint8Array | undefined> {
   const { mapStyle, referenceProperties, checkedDocumentIds, bufferGeometry, priceRange } = options;

   const checkedProperties = referenceProperties.filter(prop =>
      checkedDocumentIds.includes(prop.documentID)
   );

   if (!checkedProperties.length) return undefined;

   const { exportMap, container, exportMapFacade } = await createExportMap(mapStyle);

   try {
      const { minPrice, maxPrice, minArea, maxArea } = calculateLegendValues(checkedProperties, priceRange);
      setupPriceColoredCirclesLayer(exportMap, checkedProperties, priceRange);

      if (bufferGeometry) {
         addBufferLayer(exportMap, bufferGeometry);
         setupMaskLayer(exportMap, bufferGeometry);
      }

      await fitMapToFeatures(exportMap, checkedProperties);
      await waitForMapIdle(exportMap);

      const mapCanvas = exportMap.getCanvas();
      const compositeCanvas = document.createElement('canvas');
      compositeCanvas.width = mapCanvas.width;
      compositeCanvas.height = mapCanvas.height;
      const compositeCtx = compositeCanvas.getContext('2d');

      if (!compositeCtx) {
         return await captureMapImage(exportMap);
      }

      compositeCtx.drawImage(mapCanvas, 0, 0);
      drawPriceLegend(compositeCanvas, minPrice, maxPrice, minArea, maxArea);
      return await captureCanvasImage(compositeCanvas);
   } finally {
      cleanupExportMap(exportMapFacade, container);
   }
}

// ============================================================================
// MARK: HISTOGRAM IMAGE
// ============================================================================

export async function renderJPBHistogramImage(
   histogramElement: HTMLElement
): Promise<Uint8Array | undefined> {
   const svgElement = histogramElement.querySelector("svg");
   if (!svgElement) {
      console.warn("Could not find SVG element in histogram");
      return undefined;
   }

   const svgRect = svgElement.getBoundingClientRect();
   const svgWidth = svgRect.width || 800;
   const svgHeight = svgRect.height || 400;

   const canvas = document.createElement("canvas");
   canvas.width = svgWidth;
   canvas.height = svgHeight;
   const ctx = canvas.getContext("2d");
   if (!ctx) {
      console.warn("Could not get canvas context");
      return undefined;
   }

   ctx.fillStyle = "#ffffff";
   ctx.fillRect(0, 0, canvas.width, canvas.height);

   const svgData = new XMLSerializer().serializeToString(svgElement);
   const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
   const url = URL.createObjectURL(svgBlob);

   const img = new Image();
   await new Promise<void>((resolve, reject) => {
      img.onload = () => {
         ctx.drawImage(img, 0, 0);
         URL.revokeObjectURL(url);
         resolve();
      };
      img.onerror = () => {
         URL.revokeObjectURL(url);
         reject(new Error("Failed to load SVG image"));
      };
      img.src = url;
   });

   return await captureCanvasImage(canvas);
}

// ============================================================================
// MARK: HELPER FUNCTIONS
// ============================================================================

async function createExportMap(mapStyle: any): Promise<{ exportMap: MaplibreMap; container: HTMLDivElement; exportMapFacade: MapFacadeMaplibre }> {
   const container = document.createElement("div");
   container.style.position = "absolute";
   container.style.left = "-99999px";
   container.style.top = "-99999px";
   container.style.width = "800px";
   container.style.height = "600px";
   container.style.pointerEvents = "none";
   document.body.appendChild(container);

   const exportMapFacade = new MapFacadeMaplibre(container, false, {
      mapStyle,
      interactive: false,
      hash: false,
      canvasContextAttributes: {
         preserveDrawingBuffer: true,
         powerPreference: "high-performance"
      }
   });

   const exportMap = exportMapFacade.getMapPop();

   await new Promise<void>((resolve) => {
      if ((exportMap as any).isStyleLoaded?.()) {
         resolve();
      } else {
         exportMap.once("load", () => resolve());
      }
   });

   return { exportMap, container, exportMapFacade };
}

function cleanupExportMap(exportMapFacade: MapFacadeMaplibre | undefined, container: HTMLDivElement) {
   if (exportMapFacade) {
      try {
         exportMapFacade.getMapPop().remove();
      } catch {
         // ignore
      }
   }
   if (container.parentNode) {
      container.parentNode.removeChild(container);
   }
}

function addBufferLayer(map: MaplibreMap, bufferGeometry: any) {
   const SOURCE_ID = "jpb-export-search-area";
   const OUTLINE_ID = "jpb-export-search-area-outline";

   if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
         type: "geojson",
         data: bufferGeometry
      } as any);
   }

   if (!map.getLayer(OUTLINE_ID)) {
      map.addLayer({
         id: OUTLINE_ID,
         type: "line",
         source: SOURCE_ID,
         paint: {
            "line-color": "#d6c251",
            "line-width": 2
         }
      } as any);
   }
}

async function fitMapToFeatures(map: MaplibreMap, properties: JPBReferenceProperty[]) {
   const features = properties.map((prop) => {
      if (prop.geoshape) {
         return feature(prop.geoshape);
      }
      return feature({
         type: "Point",
         coordinates: [prop.coordinates.lng, prop.coordinates.lat]
      });
   });

   const bounds = bbox(featureCollection(features as any));
   map.fitBounds(bounds as any, {
      padding: 40,
      maxZoom: 16
   });

   await waitForMapIdle(map);
}

async function waitForMapIdle(map: MaplibreMap, delay: number = 0) {
   await new Promise<void>((resolve) => {
      map.once("idle", () => {
         if (delay > 0) {
            setTimeout(() => resolve(), delay);
         } else {
            resolve();
         }
      });
   });
}

async function captureMapImage(map: MaplibreMap): Promise<Uint8Array | undefined> {
   const canvas = map.getCanvas();
   const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png");
   });
   if (!blob) return undefined;
   const buf = await blob.arrayBuffer();
   return new Uint8Array(buf);
}

async function captureCanvasImage(canvas: HTMLCanvasElement): Promise<Uint8Array | undefined> {
   const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/png");
   });
   if (!blob) return undefined;
   const buf = await blob.arrayBuffer();
   return new Uint8Array(buf);
}

function calculateLegendValues(
   properties: JPBReferenceProperty[],
   priceRange?: { min: number; max: number }
) {
   let minPrice = priceRange?.min;
   let maxPrice = priceRange?.max;
   if (minPrice === undefined || maxPrice === undefined) {
      const prices = properties
         .map(p => p.pricePerSquareMeter)
         .filter(p => p !== null && p !== undefined && !isNaN(p));
      if (prices.length > 0) {
         minPrice = minPrice ?? Math.min(...prices);
         maxPrice = maxPrice ?? Math.max(...prices);
      } else {
         minPrice = 0;
         maxPrice = 100;
      }
   }

   const areas = properties
      .map(p => p.area)
      .filter(a => a !== null && a !== undefined && !isNaN(a) && a > 0);
   const minArea = areas.length > 0 ? Math.min(...areas) : undefined;
   const maxArea = areas.length > 0 ? Math.max(...areas) : undefined;

   return { minPrice: minPrice!, maxPrice: maxPrice!, minArea, maxArea };
}

// ============================================================================
// HELPER FUNCTIONS - Map layers
// ============================================================================

function setupPriceColoredCirclesLayer(
   map: MaplibreMap,
   properties: JPBReferenceProperty[],
   priceRange?: { min: number; max: number }
) {
   const SOURCE_ID = 'jpb-export-price-circles';
   const LAYER_ID = 'jpb-export-price-circles-layer';

   let minPrice = priceRange?.min;
   let maxPrice = priceRange?.max;
   if (minPrice === undefined || maxPrice === undefined) {
      const prices = properties
         .map(p => p.pricePerSquareMeter)
         .filter(p => p !== null && p !== undefined && !isNaN(p));
      if (prices.length > 0) {
         minPrice = minPrice ?? Math.min(...prices);
         maxPrice = maxPrice ?? Math.max(...prices);
      } else {
         minPrice = 0;
         maxPrice = 100;
      }
   }

   const areas = properties
      .map(p => p.area)
      .filter(a => a !== null && a !== undefined && !isNaN(a) && a > 0);
   const minArea = areas.length > 0 ? Math.min(...areas) : 0;
   const maxArea = areas.length > 0 ? Math.max(...areas) : 10000;
   const areaRangeSize = maxArea - minArea || 1;

   const geoJson = {
      type: "FeatureCollection" as const,
      features: properties.map((prop) => {
         const price = prop.pricePerSquareMeter || minPrice;
         const area = prop.area || minArea;

         const color = getPriceColor(price, minPrice!, maxPrice!);
         const normalizedArea = Math.max(0, Math.min(1, (area - minArea) / areaRangeSize));
         const radius = 5 + (normalizedArea * 25);

         return {
            id: prop.documentID,
            type: "Feature" as const,
            geometry: {
               type: "Point" as const,
               coordinates: [prop.coordinates.lng, prop.coordinates.lat]
            },
            properties: {
               documentID: prop.documentID,
               pricePerSquareMeter: price,
               area: area,
               color: color,
               radius: radius
            }
         };
      })
   };

   if (map.getSource(SOURCE_ID)) {
      const source = map.getSource(SOURCE_ID) as GeoJSONSource;
      if (source) source.setData(geoJson as any);
   } else {
      map.addSource(SOURCE_ID, {
         type: 'geojson',
         promoteId: 'documentID',
         data: geoJson as any
      });
   }

   if (!map.getLayer(LAYER_ID)) {
      const circleLayerSpec: any = {
         id: LAYER_ID,
         type: 'circle',
         source: SOURCE_ID,
         paint: {
            'circle-color': ['get', 'color'],
            'circle-radius': ['get', 'radius'],
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.85
         }
      };
      map.addLayer(circleLayerSpec);
   }
}

function getPriceColor(price: number, minPrice: number, maxPrice: number): string {
   const priceRangeSize = maxPrice - minPrice || 1;
   const normalizedPrice = Math.max(0, Math.min(1, (price - minPrice) / priceRangeSize));
   const red = 255;
   const green = Math.round(255 * (1 - normalizedPrice));
   const blue = 0;
   return `rgb(${red}, ${green}, ${blue})`;
}

function setupMaskLayer(map: MaplibreMap, bufferGeometry: any) {
   if (!bufferGeometry) {
      if (map.getSource(MASK_SOURCE_ID)) {
         const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
         if (source) {
            source.setData({
               type: 'FeatureCollection',
               features: []
            } as any);
         }
      }
      return;
   }

   try {
      if (!map.getSource(MASK_SOURCE_ID)) {
         map.addSource(MASK_SOURCE_ID, {
            type: 'geojson',
            data: {
               type: 'FeatureCollection',
               features: []
            }
         });
      }

      if (!map.getLayer(MASK_LAYER_ID)) {
         const maskLayerSpec: any = {
            id: MASK_LAYER_ID,
            type: 'fill',
            source: MASK_SOURCE_ID,
            paint: {
               'fill-color': '#000000',
               'fill-opacity': 0.15
            }
         };
         const outlineLayerId = "jpb-export-search-area-outline";
         if (map.getLayer(outlineLayerId)) {
            map.addLayer(maskLayerSpec, outlineLayerId);
         } else {
            map.addLayer(maskLayerSpec);
         }
      }

      const worldBbox: [number, number, number, number] = [-180, -90, 180, 90];
      const worldPolygon = bboxPolygon(worldBbox);

      let bufferFeature = bufferGeometry;
      if (bufferGeometry.type === 'FeatureCollection' && bufferGeometry.features && bufferGeometry.features.length > 0) {
         bufferFeature = bufferGeometry.features[0];
      } else if (bufferGeometry.type === 'Feature') {
         bufferFeature = bufferGeometry;
      } else if (bufferGeometry.type === 'Polygon' || bufferGeometry.type === 'MultiPolygon') {
         bufferFeature = {
            type: 'Feature',
            geometry: bufferGeometry,
            properties: {}
         };
      }

      const maskGeometry = difference(featureCollection([worldPolygon, bufferFeature]));

      if (maskGeometry) {
         const maskFeatureCollection = {
            type: 'FeatureCollection',
            features: [maskGeometry]
         };
         const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
         if (source) {
            source.setData(maskFeatureCollection as any);
         }
      }
   } catch (error) {
      console.warn("Error creating mask layer:", error);
   }
}

// ============================================================================
// HELPER FUNCTIONS - Canvas drawing
// ============================================================================

function drawPriceLegend(
   canvas: HTMLCanvasElement,
   minPrice: number,
   maxPrice: number,
   minArea?: number,
   maxArea?: number
) {
   const ctx = canvas.getContext('2d');
   if (!ctx) {
      console.warn('Could not get 2D context for legend drawing');
      return;
   }

   const padding = 10;
   const cornerRadius = 12;
   const legendWidth = 240;
   const colorBarHeight = 20;
   const sectionSpacing = 15;
   const maxCircleRadius = 18;
   const areaSectionMargin = minArea !== undefined && maxArea !== undefined ? 8 : 0;
   const sizeSectionHeight = minArea !== undefined && maxArea !== undefined ? 90 : 0;
   const topMargin = 8;
   const totalHeight = 12 + topMargin + colorBarHeight + sectionSpacing + areaSectionMargin + sizeSectionHeight + 10;

   const legendX = canvas.width - legendWidth - padding;
   const legendY = padding;
   const bgX = legendX;
   const bgY = legendY;

   ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
   ctx.beginPath();
   ctx.roundRect(bgX + 2, bgY + 2, legendWidth, totalHeight, cornerRadius);
   ctx.fill();

   ctx.fillStyle = 'rgba(255, 255, 255, 0.80)';
   ctx.beginPath();
   ctx.roundRect(bgX, bgY, legendWidth, totalHeight, cornerRadius);
   ctx.fill();

   ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
   ctx.lineWidth = 1;
   ctx.beginPath();
   ctx.roundRect(bgX, bgY, legendWidth, totalHeight, cornerRadius);
   ctx.stroke();

   let currentY = bgY + 12 + 8;

   ctx.fillStyle = '#333';
   ctx.font = '12px Arial';
   ctx.fillText('Pris pr. m²', bgX + 12, currentY);
   currentY += 18;

   const gradientX = bgX + 12;
   const gradientY = currentY;
   const gradientWidth = legendWidth - 24;
   const gradient = ctx.createLinearGradient(gradientX, gradientY, gradientX + gradientWidth, gradientY);

   const colorStops = [
      { pos: 0, color: 'rgb(255, 255, 0)' },
      { pos: 0.25, color: 'rgb(255, 192, 0)' },
      { pos: 0.5, color: 'rgb(255, 128, 0)' },
      { pos: 0.75, color: 'rgb(255, 64, 0)' },
      { pos: 1, color: 'rgb(255, 0, 0)' }
   ];

   colorStops.forEach(stop => {
      gradient.addColorStop(stop.pos, stop.color);
   });

   ctx.fillStyle = gradient;
   ctx.fillRect(gradientX, gradientY, gradientWidth, colorBarHeight);

   ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
   ctx.lineWidth = 1;
   ctx.strokeRect(gradientX, gradientY, gradientWidth, colorBarHeight);

   ctx.fillStyle = '#666';
   ctx.font = '11px Arial';
   ctx.textAlign = 'left';
   const minPriceText = Math.round(minPrice).toLocaleString('da-DK') + ' kr';
   ctx.fillText(minPriceText, gradientX, gradientY + colorBarHeight + 12);

   ctx.textAlign = 'right';
   const maxPriceText = Math.round(maxPrice).toLocaleString('da-DK') + ' kr';
   ctx.fillText(maxPriceText, gradientX + gradientWidth, gradientY + colorBarHeight + 12);

   currentY += colorBarHeight + 25;

   if (minArea !== undefined && maxArea !== undefined) {
      currentY += 8;

      ctx.fillStyle = '#333';
      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('Areal (m²)', bgX + 12, currentY);
      currentY += 18;

      const circleY = currentY + 4;
      const circleSpacing = 55;
      const circles = [
         { radius: 5, area: minArea },
         { radius: 11, area: (minArea + maxArea) / 2 },
         { radius: maxCircleRadius, area: maxArea }
      ];

      const totalCirclesWidth = (circles.length - 1) * circleSpacing + (circles[0].radius * 2);
      const startX = bgX + (legendWidth - totalCirclesWidth) / 2 + circles[0].radius;

      circles.forEach((circle, index) => {
         const circleX = startX + (index * circleSpacing);

         ctx.fillStyle = 'rgba(58, 142, 135, 0.3)';
         ctx.beginPath();
         ctx.arc(circleX, circleY, circle.radius, 0, Math.PI * 2);
         ctx.fill();

         ctx.strokeStyle = '#3a8e87';
         ctx.lineWidth = 1.5;
         ctx.stroke();

         ctx.fillStyle = '#666';
         ctx.font = '10px Arial';
         ctx.textAlign = 'center';
         const areaText = Math.round(circle.area).toLocaleString('da-DK') + ' m²';
         ctx.fillText(areaText, circleX, circleY + maxCircleRadius + 12);
      });
   }
}

async function drawJordbundLegend(canvas: HTMLCanvasElement): Promise<void> {
   const legendUrl = "https://geodata.fvm.dk:443/geoserver/ows?SERVICE=WMS&REQUEST=GetLegendGraphic&VERSION=1.3.0&FORMAT=image/png&SLD_VERSION=1.1.0&LAYER=Jordbunds_og_terraenforhold:Jordbundskort_2024&STYLE=";

   try {
      const img = new Image();
      img.crossOrigin = "anonymous";

      await new Promise<void>((resolve, reject) => {
         img.onload = () => resolve();
         img.onerror = () => reject(new Error("Failed to load jordbund legend image"));
         img.src = legendUrl;
      });

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const padding = 10;
      const legendWidth = Math.min(img.width, 200);
      const legendHeight = Math.min(img.height, 400);
      const scale = Math.min(legendWidth / img.width, legendHeight / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;

      const legendX = canvas.width - scaledWidth - padding;
      const legendY = padding;

      const bgPadding = 8;
      const bgX = legendX - bgPadding;
      const bgY = legendY - bgPadding;
      const bgWidth = scaledWidth + (bgPadding * 2);
      const bgHeight = scaledHeight + (bgPadding * 2);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.90)';
      ctx.beginPath();
      ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 8);
      ctx.fill();

      ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 8);
      ctx.stroke();

      ctx.drawImage(img, legendX, legendY, scaledWidth, scaledHeight);
   } catch (error) {
      console.warn("Failed to draw jordbund legend:", error);
   }
}


declare global {
   interface CanvasRenderingContext2D {
      roundRect(x: number, y: number, width: number, height: number, radius: number): void;
   }
}

if (!CanvasRenderingContext2D.prototype.roundRect) {
   CanvasRenderingContext2D.prototype.roundRect = function (x: number, y: number, width: number, height: number, radius: number) {
      this.beginPath();
      this.moveTo(x + radius, y);
      this.lineTo(x + width - radius, y);
      this.quadraticCurveTo(x + width, y, x + width, y + radius);
      this.lineTo(x + width, y + height - radius);
      this.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      this.lineTo(x + radius, y + height);
      this.quadraticCurveTo(x, y + height, x, y + height - radius);
      this.lineTo(x, y + radius);
      this.quadraticCurveTo(x, y, x + radius, y);
      this.closePath();
   };
}
